import { CumulativeBooster } from "../../../cumulative_boosters/cumulative_booster.js";
import { CumulativeBoosterID } from "../../../cumulative_boosters/cumulative_booster_id.js";
import { CumulativeBoosterService } from "../../../cumulative_boosters/cumulative_booster_service.js";
import {
  BigBallBooster,
  BigBallBoosterContext,
  FireworksBooster,
  FireworksBoosterContext,
  InfiniteBallsBooster,
  InfiniteBallsBoosterContext,
  MultiballBooster,
  MultiballBoosterContext,
  MultiballTarget,
  MineBooster,
  MineBoosterContext,
} from "../../../cumulative_boosters/boosters/index.js";
import { CumulativeBoosterState } from "../../../cumulative_boosters/cumulative_booster_state.js";
import { StatisticsStorage } from "../../../storage/statistics_storage.js";

export interface GameBoosterHost
  extends InfiniteBallsBoosterContext,
          BigBallBoosterContext,
          MultiballBoosterContext,
          FireworksBoosterContext,
          MineBoosterContext
{
  canActivateBooster( id: CumulativeBoosterID ): boolean;
  onBoosterCountChanged( id: CumulativeBoosterID, count: number ): void;
}

/** Owns the runtime lifecycle of cumulative boosters used in one level. */
export class GameBoosterManager
{
  private readonly infinite_balls: InfiniteBallsBooster;
  private readonly big_ball: BigBallBooster;
  private readonly multiball: MultiballBooster;
  private readonly fireworks: FireworksBooster;
  private readonly mine: MineBooster;
  private readonly boosters = new Map<CumulativeBoosterID, CumulativeBooster<any>>();
  private readonly prepared = new Map<CumulativeBoosterID, number>();
  private readonly used_counts = new Map<CumulativeBoosterID, number>();
  private readonly unsubscribe_inventory: () => void;
  private destroyed = false;

  public constructor( private readonly host: GameBoosterHost )
  {
    const infinite_properties = CumulativeBoosterService.getBoosterProperties(
      CumulativeBoosterID.InfiniteBalls
    );
    this.infinite_balls = new InfiniteBallsBooster(
      infinite_properties?.duration_seconds
    );

    const big_ball_properties = CumulativeBoosterService.getBoosterProperties(
      CumulativeBoosterID.BigBall
    );
    this.big_ball = new BigBallBooster(
      big_ball_properties?.size_multiplier,
      big_ball_properties?.mass_multiplier,
      big_ball_properties?.shots_per_use
    );

    const multiball_properties = CumulativeBoosterService.getBoosterProperties(
      CumulativeBoosterID.Multiball
    );
    this.multiball = new MultiballBooster(
      multiball_properties?.ball_count,
      multiball_properties?.shot_interval_seconds
    );

    const fireworks_properties = CumulativeBoosterService.getBoosterProperties(
      CumulativeBoosterID.Fireworks
    );
    this.fireworks = new FireworksBooster(
      fireworks_properties?.firework_count,
      fireworks_properties?.shot_interval_seconds
    );
    const mine_properties = CumulativeBoosterService.getBoosterProperties(
      CumulativeBoosterID.Mine
    );
    this.mine = new MineBooster(
      mine_properties?.mine_count,
      mine_properties?.launch_interval_seconds,
      mine_properties?.fuse_seconds
    );

    this.boosters.set( this.infinite_balls.id, this.infinite_balls );
    this.boosters.set( this.big_ball.id, this.big_ball );
    this.boosters.set( this.multiball.id, this.multiball );
    this.boosters.set( this.fireworks.id, this.fireworks );
    this.boosters.set( this.mine.id, this.mine );

    this.unsubscribe_inventory = CumulativeBoosterService.subscribe(
      ( id, count ) => this.host.onBoosterCountChanged( id, count )
    );
  }

  public getCount( id: CumulativeBoosterID ): number
  {
    return CumulativeBoosterService.inventory.getCount( id );
  }

  public getUsedCount( id: CumulativeBoosterID ): number
  {
    return this.used_counts.get( id ) ?? 0;
  }

  public resetUsedCounts(): void
  {
    this.used_counts.clear();
  }

  public hasActiveBooster(): boolean
  {
    return [ ...this.boosters.values() ].some( booster => booster.isActive );
  }

  public hasPreparedBooster(): boolean
  {
    return this.prepared.size > 0;
  }

  public getEffectValue( id: CumulativeBoosterID ): number | null
  {
    switch ( id )
    {
      case CumulativeBoosterID.Multiball:
        return this.multiball.ballCount;
      case CumulativeBoosterID.InfiniteBalls:
        return this.infinite_balls.duration;
      default:
        return null;
    }
  }

  public getMultiballMassMultiplier(): number
  {
    return this.multiball.massMultiplier;
  }

  public addUses( id: CumulativeBoosterID, amount: number ): void
  {
    CumulativeBoosterService.inventory.add( id, amount );
  }

  public activate( id: CumulativeBoosterID ): boolean
  {
    if ( CumulativeBoosterService.getActivationPhase( id ) !== "in_game" )
      return false;

    const count_before_activation = CumulativeBoosterService.inventory.getCount( id );
    if ( this.destroyed )
      return false;

    if ( !CumulativeBoosterService.isUnlocked( id, StatisticsStorage.load().level ) )
      return false;

    if ( !this.host.canActivateBooster( id ) )
      return false;

    if ( CumulativeBoosterService.hasPendingMutation() )
      return false;

    const booster = this.boosters.get( id );
    if ( booster === undefined || booster.isActive )
      return false;

    const inventory = CumulativeBoosterService.inventory;
    if ( !inventory.tryConsume( id ) )
      return false;

    booster.reset();
    try
    {
      const activated = booster.activate( this.host );
      if ( !activated )
      {
        inventory.add( id, 1 );
        return false;
      }

      this.prepared.set( id, count_before_activation );
      return true;
    }
    catch ( error )
    {
      inventory.add( id, 1 );
      booster.reset();
      throw error;
    }
  }

  /**
   * Activates and commits the one booster selected before the level.
   * Resolves only after its temporary startup effect has finished, allowing the
   * host to keep shots and in-game boosters disabled for the whole sequence.
   */
  public async activateLevelStartBooster( id: CumulativeBoosterID ): Promise<boolean>
  {
    if ( this.destroyed || CumulativeBoosterService.getActivationPhase( id ) !== "level_start" )
      return false;
    if ( [ ...this.boosters.values() ].some( booster =>
      booster.isActive && CumulativeBoosterService.getActivationPhase( booster.id ) === "level_start" ) )
      return false;
    if ( !CumulativeBoosterService.isUnlocked( id, StatisticsStorage.load().level ) )
      return false;
    if ( CumulativeBoosterService.hasPendingMutation() || !this.host.canActivateBooster( id ) )
      return false;

    const booster = this.boosters.get( id );
    const inventory = CumulativeBoosterService.inventory;
    const count_before_activation = inventory.getCount( id );
    if ( booster === undefined || !inventory.tryConsume( id ) )
      return false;

    booster.reset();
    const effect_finished = new Promise<void>( resolve =>
    {
      booster.onStateChanged = ( _, state ) =>
      {
        if ( state === CumulativeBoosterState.Completed || state === CumulativeBoosterState.Cancelled )
          resolve();
      };
    } );

    try
    {
      if ( !booster.activate( this.host ) )
      {
        this.restoreReservedCount( id, count_before_activation );
        return false;
      }

      const consumed = await CumulativeBoosterService.consume( id );
      if ( !consumed )
      {
        booster.cancel();
        this.restoreReservedCount( id, count_before_activation );
        return false;
      }

      this.used_counts.set( id, this.getUsedCount( id ) + 1 );
      await effect_finished;
      return !this.destroyed;
    }
    catch ( error )
    {
      booster.cancel();
      this.restoreReservedCount( id, count_before_activation );
      throw error;
    }
    finally
    {
      booster.onStateChanged = () => {};
    }
  }

  public consumeNow( id: CumulativeBoosterID ): boolean
  {
    if ( this.destroyed || CumulativeBoosterService.getActivationPhase( id ) !== "in_game" )
      return false;

    if ( !CumulativeBoosterService.isUnlocked( id, StatisticsStorage.load().level ) )
      return false;

    if ( !this.host.canActivateBooster( id ) || CumulativeBoosterService.hasPendingMutation() )
      return false;

    if ( !CumulativeBoosterService.inventory.tryConsume( id ) )
      return false;

    this.used_counts.set( id, this.getUsedCount( id ) + 1 );
    void CumulativeBoosterService.consume( id );
    return true;
  }

  public cancelAndRefund( id: CumulativeBoosterID ): boolean
  {
    const booster = this.boosters.get( id );
    if ( booster === undefined || !booster.isActive )
      return false;

    const count_before_activation = this.prepared.get( id );
    this.prepared.delete( id );
    booster.cancel();
    if ( count_before_activation !== undefined )
      this.restoreReservedCount( id, count_before_activation );
    return true;
  }

  public startInfiniteBallsCountdown(): boolean
  {
    return this.infinite_balls.startTimer();
  }

  public onPrimaryBallFired( target: MultiballTarget ): void
  {
    this.big_ball.onBallFired();
    this.multiball.onPrimaryBallFired( target );
    this.fireworks.onPrimaryFireworkFired( target );

    for ( const id of this.prepared.keys() )
    {
      this.used_counts.set( id, this.getUsedCount( id ) + 1 );
      void CumulativeBoosterService.consume( id );
    }

    this.prepared.clear();
  }

  public update( dt: number ): void
  {
    if ( this.destroyed )
      return;

    for ( const booster of this.boosters.values() )
      booster.update( dt );
  }

  public destroy(): void
  {
    if ( this.destroyed )
      return;
    this.destroyed = true;
    this.unsubscribe_inventory();

    const prepared = [ ...this.prepared.entries() ];
    this.prepared.clear();
    for ( const [ id, count_before_activation ] of prepared )
      this.restoreReservedCount( id, count_before_activation );

    for ( const booster of this.boosters.values() )
      booster.cancel();
  }

  private restoreReservedCount(
    id: CumulativeBoosterID,
    count_before_activation: number
  ): void
  {
    const inventory = CumulativeBoosterService.inventory;
    inventory.setCount(
      id,
      Math.max( inventory.getCount( id ), count_before_activation )
    );
  }
}
